
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Eye, CheckCircle, XCircle, AlertTriangle, Music, Camera, FileText } from "lucide-react";

const ContentModeration = () => {
  const pendingContent = [
    {
      id: 1,
      title: "New Amapiano Mix - Weekend Vibes",
      user: "DJ Maphorisa",
      type: "audio",
      uploadDate: "2024-01-15",
      status: "pending",
      reportCount: 0
    },
    {
      id: 2,
      title: "Club Event Photography",
      user: "Lens by Lerato",
      type: "image",
      uploadDate: "2024-01-15",
      status: "pending",
      reportCount: 2
    },
    {
      id: 3,
      title: "Event Announcement Post",
      user: "MC Thabo",
      type: "text",
      uploadDate: "2024-01-14",
      status: "flagged",
      reportCount: 5
    }
  ];

  const getContentIcon = (type: string) => {
    switch (type) {
      case "audio": return <Music className="w-4 h-4" />;
      case "image": return <Camera className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending": 
        return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">Pending Review</Badge>;
      case "flagged":
        return <Badge variant="destructive" className="bg-red-500/20 text-red-400">Flagged</Badge>;
      case "approved":
        return <Badge variant="default" className="bg-green-500/20 text-green-400">Approved</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-white/5 border-white/20">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-white font-medium">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-400">23</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/20">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-white font-medium">Flagged Content</p>
                <p className="text-2xl font-bold text-red-400">8</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/20">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">Approved Today</p>
                <p className="text-2xl font-bold text-green-400">156</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/20">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-white font-medium">Rejected Today</p>
                <p className="text-2xl font-bold text-red-400">12</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/5 border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Content Awaiting Moderation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pendingContent.map((content) => (
              <div key={content.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-white to-gray-400 rounded-lg flex items-center justify-center text-black">
                    {getContentIcon(content.type)}
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{content.title}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-gray-400 text-sm">by {content.user}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400 text-sm">{content.uploadDate}</span>
                      {content.reportCount > 0 && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-red-400 text-sm">{content.reportCount} reports</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(content.status)}
                  <div className="flex items-center space-x-2">
                    <Button size="sm" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                      Review
                    </Button>
                    <Button size="sm" className="bg-green-500 text-white hover:bg-green-600">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button size="sm" variant="destructive" className="bg-red-500 text-white hover:bg-red-600">
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentModeration;
